import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { w as waitForAnimation } from './index3.js';
import { c as createPopper, d as destroyPopper } from './index4.js';

const menuPaneCss = "@keyframes anim-open{0%{opacity:0}10%{opacity:0}to{opacity:1}}@keyframes anim-close{0%{opacity:1}90%{opacity:0}to{opacity:0}}:host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:none;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;position:absolute;z-index:var(--jw-z-index-menus)}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host([open]){animation:anim-open .25s ease forwards}:host([open][data-popper-placement^=top]) .content{animation:\"a\" .25s ease forwards}@keyframes a{0%{transform:translateY(16px)}to{transform:translateY(0)}}:host([open][data-popper-placement^=right]) .content{animation:\"b\" .25s ease forwards}@keyframes b{0%{transform:translateX(-16px)}to{transform:translateX(0)}}:host([open][data-popper-placement^=bottom]) .content{animation:\"c\" .25s ease forwards}@keyframes c{0%{transform:translateY(-16px)}to{transform:translateY(0)}}:host([open][data-popper-placement^=left]) .content{animation:\"d\" .25s ease forwards}@keyframes d{0%{transform:translateX(16px)}to{transform:translateX(0)}}:host(:not([open])){animation:anim-close .25s ease forwards}:host(:not([open])[data-popper-placement^=top]) .content{animation:\"e\" .25s ease forwards}@keyframes e{0%{transform:translateY(0)}to{transform:translateY(16px)}}:host(:not([open])[data-popper-placement^=right]) .content{animation:\"f\" .25s ease forwards}@keyframes f{0%{transform:translateX(0)}to{transform:translateX(-16px)}}:host(:not([open])[data-popper-placement^=bottom]) .content{animation:\"g\" .25s ease forwards}@keyframes g{0%{transform:translateY(0)}to{transform:translateY(-16px)}}:host(:not([open])[data-popper-placement^=left]) .content{animation:\"h\" .25s ease forwards}@keyframes h{0%{transform:translateX(0)}to{transform:translateX(16px)}}:host([data-popper-placement^=left]),:host([data-popper-placement^=right]){flex-direction:row}:host([data-popper-placement^=bottom]),:host([data-popper-placement^=top]){flex-direction:column}:host([data-popper-placement$=start]) .content{align-self:flex-start}:host([data-popper-placement$=stretch]) .content{align-self:stretch}:host([data-popper-placement$=end]) .content{align-self:flex-end}:host([stretch=fixed][data-popper-placement^=left]) .content,:host([stretch=fixed][data-popper-placement^=right]) .content{overflow-y:scroll}:host([stretch=fixed][data-popper-placement^=bottom]) .content,:host([stretch=fixed][data-popper-placement^=top]) .content{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.content{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;background-color:var(--jw-theme-surface-elevated-default-bg-color);border:1px solid var(--jw-theme-surface-border-border-color);border-radius:.25rem;box-shadow:var(--jw-theme-effects-shadow-shadow-lg-box-shadow);box-sizing:border-box;cursor:default!important;display:flex;flex:1;flex-direction:column;font-family:Open Sans;font-size:.875rem;font-style:normal;font-weight:400;height:100%;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;margin:0;min-height:32px;min-width:32px;padding:0;text-transform:none;width:100%}.content *,.content :after,.content :before{box-sizing:inherit}.content img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}::slotted(wui-check-list-input),::slotted(wui-list){padding:.5rem;width:auto}";
const WuiMenuPaneStyle0 = menuPaneCss;

const WuiMenuPane = /*@__PURE__*/ proxyCustomElement(class WuiMenuPane extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.clickOutside = createEvent(this, "clickOutside", 3);
        this.openChanged = createEvent(this, "openChanged", 3);
        this.handleClickOutside = (e) => {
            const composedPath = e.composedPath();
            const target = composedPath[0];
            if (!target) {
                return;
            }
            if (composedPath.includes(this.el) || this.el.contains(target)) {
                return;
            }
            this.clickOutside.emit(e);
        };
        this.open = false;
        this.placement = 'auto';
        this.stretch = 'min';
    }
    watchOpen(newValue) {
        if (newValue) {
            this.show();
        }
        else {
            this.hide();
        }
    }
    _listenKeyDown(e) {
        this.handleKeyDown(e);
    }
    /**
     * Forward a keydown KeyboardEvent to be handled by this component.
     *
     * Handles Escape to close menu.
     * Handles arrow up/down and Enter to open menu.
     *
     * @param e KeyboardEvent (should be keydown)
     * @returns Returns true when the event was handled
     */
    async handleKeyDown(e) {
        // ⚠️ When successfully handling a keyboard event, use `preventDefault` to stop it from doing anything else
        // Close menu
        if (this.open && e.key === 'Escape') {
            this.hide();
            e.preventDefault();
            e.stopPropagation();
            return true;
        }
        // Open menu
        const openKeys = ['ArrowDown', 'ArrowUp', 'Enter'];
        if (!this.open && openKeys.includes(e.key)) {
            this.show();
            e.preventDefault();
            e.stopPropagation();
            return true;
        }
        return false;
    }
    show() {
        const p = waitForAnimation(this.el, 'animation').then(() => {
            document.addEventListener('click', this.handleClickOutside);
        });
        // reset display to make sure el.offsetParent is correct because
        // popper needs it calculate position
        this.el.style.display = 'none';
        this.popperInstance = createPopper(this.el, {
            placement: this.placement,
            stretch: this.stretch,
            offset: 4,
        });
        this.el.style.display = 'flex';
        this.open = true;
        this.openChanged.emit(true);
        return p;
    }
    hide() {
        document.removeEventListener('click', this.handleClickOutside);
        const p = waitForAnimation(this.el, 'animation').then(() => {
            if (!this.open) {
                this.el.style.display = 'none';
                this.popperInstance = destroyPopper(this.popperInstance);
            }
        });
        this.open = false;
        this.openChanged.emit(false);
        return p;
    }
    async disconnectedCallback() {
        if (this.open) {
            await this.hide();
        }
    }
    componentDidLoad() {
        if (this.open) {
            this.show();
        }
    }
    render() {
        return (h(Host, { key: '0ed1620b165298d663d5604027c4d58c21ecb522', role: "menu" }, h("div", { key: '8f9f1dd89e4a0712f72cbac0d5990225a6d568b0', class: "content" }, h("slot", { key: '93b4877f8f54db136cd14d1a3cc70910b5f74ea8' }))));
    }
    get el() { return this; }
    static get watchers() { return {
        "open": ["watchOpen"]
    }; }
    static get style() { return WuiMenuPaneStyle0; }
}, [1, "wui-menu-pane", {
        "open": [1540],
        "placement": [1],
        "stretch": [513],
        "handleKeyDown": [64]
    }, [[0, "keydown", "_listenKeyDown"]], {
        "open": ["watchOpen"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-menu-pane"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiMenuPane);
            }
            break;
    } });
}
defineCustomElement();

export { WuiMenuPane as W, defineCustomElement as d };

//# sourceMappingURL=menu-pane.js.map